import type { QualityLevel } from '@/types/conditions';

/**
 * conditionRatingDisplay — the SINGLE display-rename boundary for condition
 * ratings (DASH-R1 / issue #785).
 *
 * Immutable-internal-key invariant (wave-plan decision #3): the model values
 * stay `excellent`/`good`/`fair`/`poor` (the `QualityLevel` keys) plus the
 * variable key `crepuscular`. ONLY the displayed WORD is renamed here —
 * `excellent → "Prime"` and `crepuscular → "Hunt Window"`. No `QualityLevel`
 * member, no `ConditionsQuality*` type name, no stored/synced row is renamed.
 * This map is the one place a display word is produced; do NOT introduce a
 * second display word/token map anywhere.
 *
 * Each tier entry carries a display `word` + the ramp `token` (the W1 ramp
 * table). `crepuscular` is a VARIABLE label, not a tier — it carries a `word`
 * only (no rating token).
 */

/** A tier display entry: rename word plus the ramp color token. */
interface RatingDisplayEntry {
  word: string;
  token: string;
}

/** The crepuscular variable display entry: rename word only (no rating token). */
interface VariableDisplayEntry {
  word: string;
}

/**
 * ONE exported display map. The four `QualityLevel` keys are exhaustively
 * covered via the `Record<QualityLevel, RatingDisplayEntry>` intersection — a
 * type error fires if `QualityLevel` gains/loses a member. The `crepuscular`
 * key is the additional variable label carried in the same single object (NOT
 * split into a second constant, per decision #3).
 */
export const CONDITION_RATING_DISPLAY: Record<QualityLevel, RatingDisplayEntry> & {
  crepuscular: VariableDisplayEntry;
} = {
  poor: { word: 'Poor', token: '--color-rating-poor' },
  fair: { word: 'Fair', token: '--color-rating-fair' },
  good: { word: 'Good', token: '--color-rating-good' },
  // Display-only rename: model value stays `excellent`; the word is "Prime".
  excellent: { word: 'Prime', token: '--color-rating-prime' },
  // Variable display-rename: `crepuscular` is never user-visible — "Hunt Window".
  crepuscular: { word: 'Hunt Window' },
};
